import { AddDeviceInput } from "../../API";
import { getDeviceSuffix } from "./generateDeviceName";

// Device template data used by both the SKU flow (Tier 2/3) and legacy device template flow.
//
// SKU flow populates: name, makeModelId, models, isPT (from catalog + config)
// Legacy template flow populates: deviceType, model, nodeNumber (from DDB template records)
//
// Both patterns are supported for backward compatibility.
export interface DeviceTemplateData {
  // --- SKU flow fields (Tier 1/2 catalog-driven) ---
  name?: string; // component name from catalog (e.g. "Ch4", "Optical 1")
  makeModelId?: string; // from skuCatalog (CC_AGX, CC_CH4, CC_OPT, CC_THERMAL, CC_LWOP, CC_VOC, CC_POE)
  models?: string[]; // model assignments from configuration

  // --- Legacy template flow fields ---
  deviceType?: string; // DDB DeviceMake model string (e.g. "CC CH4", "CC FB4")
  model?: string[]; // model assignments (legacy field name)

  // --- Shared fields ---
  deviceIp?: string;
  isPT?: boolean;
  panTiltType?: string;
  panTiltIP?: string;
  hasRecordVideo?: boolean;
  nodeNumber?: number; // required in legacy flow for explicit node assignment
  password?: string;
}

const getRtspUrl = (
  makeModelId: string,
  ip: string,
  password: string
): string => {
  switch (makeModelId) {
    // New format
    case "CC_CH4":
    case "CC_LWOP":
    case "CC_VOC":
      return `rtsp://${password}@${ip}/live1`;
    case "CC_OPT":
      return `rtsp://${password}@${ip}/cam/realmonitor?channel=1&subtype=1`;
    case "CC_THERMAL":
      return `rtsp://${password}@${ip}/cam/realmonitor?channel=2&subtype=1`;
    // Legacy format
    case "CC CH4":
    case "V1":
      return makeModelId === "V1"
        ? `rtsp://${ip}:554/live?channel=0&subtype=0`
        : `rtsp://${password}@${ip}/live1`;
    case "CC FB4":
      return `rtsp://${password}@${ip}/cam/realmonitor?channel=1&subtype=1`;
    case "CC FB4 Thermal":
      return `rtsp://${password}@${ip}/cam/realmonitor?channel=2&subtype=1`;
    default:
      return ""; // CC_AGX, CC_POE — no camera stream
  }
};

interface SelectedLocation {
  value: string;
  title: string;
}

interface SelectedNode {
  nodeId: string;
  nodeName: string;
  nodeIP: string;
}

interface MapTemplateOptions {
  templateData: DeviceTemplateData[];
  selectedCustomerId: string;
  selectedLocation: SelectedLocation | null;
  selectedNodes: SelectedNode[];
  devicePassword: string;
  skuNumber?: string;
  configurationName?: string;
  serialNumber?: string;
  componentSerialNumbers?: string[];
}

export const mapTemplateDataToDeviceInput = ({
  templateData,
  selectedCustomerId,
  selectedLocation,
  selectedNodes,
  devicePassword,
  skuNumber,
  configurationName,
  serialNumber,
  componentSerialNumbers,
}: MapTemplateOptions): AddDeviceInput[] => {
  const devices: AddDeviceInput[] = [];
  const suffixCounts: Record<string, number> = {};

  templateData.forEach((template, index) => {
    const {
      // Resolve fields supporting both SKU and legacy formats
      makeModelId: newMakeModelId,
      deviceType: legacyDeviceType,
      // models: newModels,
      // model: legacyModel,
      deviceIp = "",
      nodeNumber,
      isPT,
      panTiltType,
      panTiltIP,
      hasRecordVideo,
      password: templatePassword,
    } = template;

    // Use SKU catalog makeModelId if available, otherwise fall back to legacy deviceType
    const effectiveMakeModelId = newMakeModelId ?? legacyDeviceType ?? "";
    // Use new models array if available, otherwise fall back to legacy model array
    // const effectiveModels = newModels ?? legacyModel ?? [];

    const effectivePanTiltIP = panTiltIP || deviceIp;
    const effectivePassword = templatePassword || devicePassword;

    const locationTitle = selectedLocation?.title?.replace(/\s+/g, "") || "";
    const locationId = selectedLocation?.value ?? "";

    // For SKU flow: one node entry per device (direct index mapping)
    // For legacy template flow (has nodeNumber): use nodeNumber - 1
    const nodeIndex = nodeNumber !== undefined ? nodeNumber - 1 : index;
    const selectedNode = selectedNodes[nodeIndex];
    const nodeId = selectedNode?.nodeId ?? "";
    const nodeName = selectedNode?.nodeName ?? "";
    const nodeIp = selectedNode?.nodeIP ?? "";

    const componentSerialNumber = componentSerialNumbers?.[index] || undefined;

    const suffix = getDeviceSuffix(effectiveMakeModelId);

    suffixCounts[suffix] = (suffixCounts[suffix] || 0) + 1;
    const count = suffixCounts[suffix];

    const sourceVideo = getRtspUrl(
      effectiveMakeModelId,
      deviceIp,
      effectivePassword
    );

    const hasPanTilt =
      isPT ??
      (effectiveMakeModelId === "CC CH4" ||
        effectiveMakeModelId === "CC_CH4" ||
        effectiveMakeModelId === "V1");

    const baseDevice: AddDeviceInput = {
      customerId: selectedCustomerId,
      locationId,
      locationName: selectedLocation?.title,
      nodeId,
      nodeName,
      makeModelId: effectiveMakeModelId,
      cameraIpAddress: deviceIp,
      name: `${locationTitle}-${suffix}-${count}`,
      ...(skuNumber && { skuNumber }),
      ...(configurationName && { configurationName }),
      ...(serialNumber && { serialNumber }),
      ...(componentSerialNumber && { componentSerialNumber }),
      ...((skuNumber || serialNumber) && { onboardedAt: Date.now() }),
      deviceData: JSON.stringify({
        hasPanTilt,
        ...(hasPanTilt && {
          panTiltIP: effectivePanTiltIP,
          panTiltType: panTiltType || "",
        }),
        recentFrame: "",
        keyFrame: "",
        ...(sourceVideo && {
          rtspHost: `rtsp://${nodeIp}/${locationTitle}-${suffix}`,
          sourceVideo,
        }),
        hasZoom:
          effectiveMakeModelId === "CC_CH4" ||
          effectiveMakeModelId === "CC CH4",
        protocol: "",
        hasRecordVideo: hasRecordVideo ?? true,
      }),
    };

    devices.push(baseDevice);
  });

  return devices;
};
